import PropTypes from 'prop-types'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

// Internal components
import ArticleList from '../../components/ArticleList/ArticleList'
import BannerBlock from '../../components/BannerBlock/BannerBlock'
import Correlated from '../../components/Correlated/Correlated'
import DownloadList from '../../components/DownloadList/DownloadList'
import FigureLandscape from '../../components/FigureLandscape/FigureLandscape'
import FigurePortrait from '../../components/FigurePortrait/FigurePortrait'
import Foldout from '../../components/Foldout/Foldout'
import IndexList from '../../components/IndexList/IndexList.js'
import LayoutInfo from '../../components/LayoutInfo/LayoutInfo'
import LinkBlock from '../../components/LinkBlock/LinkBlock'
import PageCover from '../../components/PageCover/PageCover'
import RichTextBlock from '../../components/RichTextBlock/RichTextBlock'
import ShareOptions from '../../components/ShareOptions/ShareOptions'
import SideMenu from '../../components/SideMenu/SideMenu'
import Vcard from '../../components/Vcard/Vcard'
import YoutubeEmbed from '../../components/YoutubeEmbed/YoutubeEmbed'

// Utility functions
import getSiteHandle from '../../utils/i18n/getSiteHandle'
import queryServerSideData from '../../utils/data/queryServerSideData'
import shareOptionsData from '../../utils/data/shareOptions.json'

// Static cookie to avoid multiple PHP sessions, used on build time
const staticCookie = 'CraftSessionId=rinus_static; RINUS_CSRF=null;'

const InfoPage = (props) => {
  const { entry, sideMenu } = props

  return (
    <>
      {entry?.cover && <PageCover figures={entry.cover} modifiers={['PageCover--info']} />}
      {sideMenu && sideMenu?.[0] && <SideMenu menu={sideMenu[0]} />}
      <LayoutInfo
        mainContent={
          <>
            {entry?.title && <RichTextBlock data={[{ h1: entry.title }]} />}
            {entry?.introText && <RichTextBlock content={entry.introText} modifiers={['RichTextBlock--intro']} />}
            {entry?.componentBlocks &&
              entry.componentBlocks.map((component) => {
                switch (component.typeHandle) {
                  case 'wysiwyg':
                    return <RichTextBlock {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'figureLandscape':
                    return <FigureLandscape {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'figurePortrait':
                    return <FigurePortrait {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'linkblock':
                    return <LinkBlock {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'foldout':
                    return <Foldout {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'downloadlist':
                    return <DownloadList {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'vcard':
                    return <Vcard {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'youtubeEmbed':
                    return <YoutubeEmbed {...component} key={`${component.typeHandle}-${component.id}`} />
                  case 'indexlist':
                    return <IndexList {...component} key={`${component.typeHandle}-${component.id}`} />
                  default:
                    return ''
                }
              })}
            <ShareOptions {...shareOptionsData} modifiers={['ShareOptions--infopage']} />
          </>
        }
        footerContent={
          <>
            {entry?.bannerBlockInfopage.length && (
              <BannerBlock
                {...entry.bannerBlockInfopage[0]}
                modifiers={['BannerBlock--layoutInfo BannerBlock--isContained']}
              />
            )}
          </>
        }
      />
      <>
        {entry?.correlatedInfopages.length ? <Correlated {...entry.correlatedInfopages[0]} /> : ''}
        {entry?.articleListInfopage.length ? <ArticleList {...entry.articleListInfopage[0]} /> : ''}
      </>
    </>
  )
}

export async function getStaticPaths() {
  let entries = []

  try {
    const staticPathsJson = await queryServerSideData('qInfopagePaths.graphql', {}, staticCookie)
    entries = staticPathsJson?.data?.entries || []
  } catch (err) {
    console.error('Failed to fetch static paths:', err)
  }

  const paths = entries.map((page) => {
    const slugs = page.uri.split('/')
    slugs.shift()
    return { params: { slug: slugs } }
  })

  return {
    paths,
    fallback: true
  }
}

export async function getStaticProps(props) {
  const { params, locale } = props
  const siteHandle = getSiteHandle(locale)
  const globalJson = await queryServerSideData(
    'qGlobal.graphql',
    { site: siteHandle, seoUri: '/', seoSite: siteHandle },
    staticCookie
  )

  const infoDetailJson = await queryServerSideData(
    'qInfopageDetail.graphql',
    { slug: params.slug[params.slug.length - 1], site: siteHandle },
    staticCookie
  )

  if (infoDetailJson.data?.entry === null) {
    return {
      notFound: true
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale || process.env.NEXT_PUBLIC_RINUS_DEFAULT_LANGUAGE, process.env.NEXT_PUBLIC_RINUS_NAMESPACES?.split(','))),
      ...globalJson.data,
      ...infoDetailJson.data
    },
    revalidate: 60
  }
}

InfoPage.propTypes = {
  entry: PropTypes.object,
  sideMenu: PropTypes.array
}

export default InfoPage
